/**
 * PAPPO — Auth Context Provider
 * Gere o estado de autenticação globalmente via React Context.
 * Fornece user, login, register, logout, refreshUser e loading state.
 */

import {
  createContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import type { User, LoginPayload, RegisterPayload } from '../types/user';
import * as authService from '../services/auth';
import { getMe } from '../services/users';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (payload: LoginPayload) => Promise<{ success: boolean; error?: string }>;
  register: (payload: RegisterPayload) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  /** Revalida o perfil no servidor (nível/flags) e atualiza o user salvo. */
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  login: async () => ({ success: false }),
  register: async () => ({ success: false }),
  logout: () => {},
  refreshUser: async () => {},
});

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /** Busca o perfil no servidor e mescla no user salvo (mata sessão stale). */
  const refreshUser = useCallback(async () => {
    if (!authService.isAuthenticated()) return;
    const res = await getMe();
    if (res.success && res.data) {
      setUser((prev) => {
        const merged = { ...(prev ?? {}), ...res.data } as User;
        localStorage.setItem('pappo_user', JSON.stringify(merged));
        return merged;
      });
    }
  }, []);

  /* Ao montar, carrega o user do localStorage e revalida com o servidor */
  useEffect(() => {
    const stored = authService.getCurrentUser();
    if (stored && authService.isAuthenticated()) {
      setUser(stored);
      refreshUser();
    }
    setIsLoading(false);
  }, [refreshUser]);

  const login = useCallback(async (payload: LoginPayload) => {
    const result = await authService.login(payload);
    if (result.success && result.data) {
      setUser(result.data.user as unknown as User);
      // A resposta de /login não traz staff_role (nem avatar assinado). Completa
      // o perfil pelo /me para o Staff/nav aparecerem já no login (sem reload).
      await refreshUser();
      return { success: true };
    }
    return { success: false, error: result.error };
  }, [refreshUser]);

  const register = useCallback(async (payload: RegisterPayload) => {
    const result = await authService.register(payload);
    if (result.success && result.data) {
      setUser(result.data.user as unknown as User);
      await refreshUser();
      return { success: true };
    }
    return { success: false, error: result.error };
  }, [refreshUser]);

  const logout = useCallback(() => {
    setUser(null);
    authService.logout();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        register,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
